// Flex result types — deliberately NOT an extension of gait's `AnalysisResult`.
// Flex is a Fitness/Wellness sub-feature (see docs/FLEX_WELLNESS_ABGRENZUNG.md),
// kept as its own parallel type so its banding/wording can never drift into
// the clinical gait vocabulary by accident.

import type { Side } from "../pose/landmarks";

export type FlexBand = "GUT" | "OK" | "EINGESCHRAENKT";
export type FlexConfidence = "high" | "medium" | "low";

export type FlexRegion =
  | "hws"
  | "schulter_brust"
  | "rumpf"
  | "huefte_hamstring"
  | "knie_sprunggelenk";

export const FLEX_REGION_LABEL: Record<FlexRegion, string> = {
  hws: "Kopf / HWS",
  schulter_brust: "Schulter / Brust",
  rumpf: "Rumpf",
  huefte_hamstring: "Hüfte / Beinrückseite",
  knie_sprunggelenk: "Knie / Sprunggelenk",
};

export interface FlexFinding {
  key: string;
  label: string;
  region: FlexRegion;
  band: FlexBand;
  /**
   * The raw measured magnitude (deg or ratio). Since 02.07.2026 (see
   * docs/FLEX_WELLNESS_ABGRENZUNG.md §3a — a deliberate Founder decision,
   * not a default) this MAY be shown to the user alongside the Ampel band,
   * always framed as a Self-vs-Self transparency value — NEVER worded as a
   * clinical finding, diagnosis, or comparison to a clinical norm table.
   */
  magnitude: number;
  unit: "deg" | "ratio";
  confidence: FlexConfidence;
  side?: Side;
  /**
   * Optional Self-vs-Self reach estimate (0-100%) for movements where a
   * relative "how close did you get" number is more intuitive than the raw
   * angle/ratio — e.g. finger-floor reach on trunk_forward_flex, as % of the
   * person's own leg length (see flex/measurements.ts). Deliberately
   * percent, not height-calibrated cm — see that module's comment for why.
   * Undefined when not applicable to this movement or too few usable frames.
   */
  reachPercent?: number;
}

export interface FlexAgeResult {
  flexAge: number;
  chronoAge: number | null;
  /** 0..100 internal wellness composite, never shown raw. */
  compositeScore: number;
  /** Delta in flexAge vs. the previous flex session, in years (negative = improved). */
  comparedToPrevious: number | null;
}

export interface FlexResult {
  sessionId: string;
  recordedAt: number;
  findings: FlexFinding[];
  flexAge: FlexAgeResult;
  versions: FlexVersions;
}

export interface FlexVersions {
  poseModel: string;
  movementCatalog: string;
  threshold: string;
  ageModel: string;
}

export const CURRENT_FLEX_VERSIONS: FlexVersions = {
  poseModel: "blazepose-full-0.10.18",
  movementCatalog: "flex-catalog-v1",
  threshold: "flex-seed-1",
  ageModel: "flex-age-seed-1",
};
